import { useState } from 'react';

import { ACTION_PLAN_SOURCE } from 'constants/actionPlan';
import { ActionPlanListItemType } from 'types/actionPlans';

import useActionPlansConfirmModal from './useActionPlansConfirmModal';
import { getActionPlanDeleteMessage } from './utils';

const sourceNeedsDeleteReason = (source: ActionPlanListItemType['source']) =>
  source === ACTION_PLAN_SOURCE.AI_COMPETITIVE_INTELLIGENCE || source === ACTION_PLAN_SOURCE.ALERTS;

type Params = {
  removePlan: (args: { actionPlanId: number; reason?: string }) => Promise<unknown>;
  isRemovingPlan: boolean;
};

const useActionPlanDeleteFlow = ({ removePlan, isRemovingPlan }: Params) => {
  const { openConfirm, confirmModalProps } = useActionPlansConfirmModal();
  const [deleteReason, setDeleteReason] = useState('');
  const [aiDeleteTarget, setAiDeleteTarget] = useState<{
    id: number;
    name: string;
    onSuccess?: () => void;
  } | null>(null);

  const openDeletePlanConfirm = ({
    id,
    name,
    source,
    onSuccess,
  }: {
    id: number;
    name: string;
    source: ActionPlanListItemType['source'];
    onSuccess?: () => void;
  }) => {
    if (sourceNeedsDeleteReason(source)) {
      setDeleteReason('');
      setAiDeleteTarget({ id, name, onSuccess });
      return;
    }

    openConfirm('Delete Action Plan?', getActionPlanDeleteMessage(name), async () => {
      await removePlan({ actionPlanId: id });
      onSuccess?.();
    });
  };

  const deleteWithReasonModalProps = {
    open: Boolean(aiDeleteTarget),
    planName: aiDeleteTarget?.name ?? '',
    loading: isRemovingPlan,
    reason: deleteReason,
    onReasonChange: setDeleteReason,
    onConfirm: async () => {
      if (!aiDeleteTarget) {
        return;
      }
      await removePlan({ actionPlanId: aiDeleteTarget.id, reason: deleteReason });
      aiDeleteTarget.onSuccess?.();
      setDeleteReason('');
      setAiDeleteTarget(null);
    },
    onCancel: () => {
      if (isRemovingPlan) {
        return;
      }
      setDeleteReason('');
      setAiDeleteTarget(null);
    },
  };

  return {
    confirmModalProps,
    deleteWithReasonModalProps,
    openDeletePlanConfirm,
  };
};

export default useActionPlanDeleteFlow;
